import { useEffect, useMemo, useRef, useState } from "react"

import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"
import { ChevronLeft, ChevronRight, Presentation } from "lucide-react"

import { notebookSlides } from "../utils/notebookSlides"
import type { Notebook } from "../utils/notebookSnapshot"
import { NotebookCanvas } from "./NotebookCanvas"
import styles from "./NotebookPresentation.module.css"
import { NotebookPresentationExport } from "./NotebookPresentationExport"

function Slide({
  notebook,
  organizationSlug,
  workspaceId,
  slide,
}: {
  notebook: Notebook
  organizationSlug: string
  workspaceId: string
  slide: ReturnType<typeof notebookSlides>[number]
}) {
  const viewport = useRef<HTMLDivElement>(null)
  const page = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)
  useEffect(() => {
    const frame = viewport.current
    const content = page.current
    if (!frame || !content) {
      return
    }
    const resize = () =>
      setScale(
        Math.min(
          frame.clientWidth / 1200,
          frame.clientHeight / Math.max(675, content.scrollHeight),
        ),
      )
    const observer = new ResizeObserver(resize)
    observer.observe(frame)
    observer.observe(content)
    resize()
    return () => observer.disconnect()
  }, [])
  return (
    <div
      ref={viewport}
      className="relative min-h-0 flex-1 overflow-hidden"
      aria-label={slide.title}
    >
      <div
        className="absolute left-1/2 top-1/2"
        style={{
          width: 1200,
          transform: `translate(-50%, -50%) scale(${scale})`,
        }}
      >
        <div ref={page} className={styles.slide}>
          <NotebookCanvas
            headingOffset={2}
            notebook={notebook}
            content={slide.content}
            organizationSlug={organizationSlug}
            workspaceId={workspaceId}
          />
        </div>
      </div>
    </div>
  )
}

export function NotebookPresentation({
  notebook,
  organizationSlug,
  workspaceId,
  disabled = false,
}: {
  notebook: Notebook
  organizationSlug: string
  workspaceId: string
  disabled?: boolean
}) {
  const slides = useMemo(() => notebookSlides(notebook), [notebook])
  const [open, setOpen] = useState(false)
  const [index, setIndex] = useState(0)
  const dialog = useRef<HTMLDivElement>(null)
  const selected = Math.min(index, Math.max(0, slides.length - 1))
  const next = () => {
    setIndex(Math.min(slides.length - 1, selected + 1))
    dialog.current?.focus()
  }
  const previous = () => {
    setIndex(Math.max(0, selected - 1))
    dialog.current?.focus()
  }
  return (
    <>
      <Button
        variant="ghost"
        disabled={disabled || !slides.length}
        onClick={() => {
          document
            .querySelectorAll<HTMLElement>("[data-notebook-canvas]")
            .forEach((canvas) => {
              if (canvas.dataset.notebookCanvas === notebook.id) {
                canvas
                  .querySelectorAll("video")
                  .forEach((video) => video.pause())
              }
            })
          setIndex(0)
          setOpen(true)
        }}
      >
        <Presentation />
        Present
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          ref={dialog}
          className="h-dvh w-screen max-w-none rounded-none p-4"
          aria-describedby={undefined}
          onKeyDownCapture={(event) => {
            if (
              event.target instanceof Element &&
              event.target.closest(
                'video, select, input, textarea, a, [role="combobox"], [role="listbox"]',
              )
            ) {
              return
            }
            if (
              event.key === " " &&
              event.target instanceof Element &&
              event.target.closest("button")
            ) {
              return
            }
            if (
              ["ArrowRight", "ArrowDown", "PageDown", " "].includes(event.key)
            ) {
              event.preventDefault()
              next()
            }
            if (["ArrowLeft", "ArrowUp", "PageUp"].includes(event.key)) {
              event.preventDefault()
              previous()
            }
            if (event.key === "Home") {
              event.preventDefault()
              setIndex(0)
            }
            if (event.key === "End") {
              event.preventDefault()
              setIndex(slides.length - 1)
            }
          }}
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            dialog.current?.focus()
          }}
        >
          <div className="flex flex-wrap items-center justify-between gap-4 pb-3">
            <DialogTitle>{notebook.title}</DialogTitle>
            <div className="flex items-start gap-3">
              <NotebookPresentationExport
                notebook={notebook}
                slides={slides}
                organizationSlug={organizationSlug}
                workspaceId={workspaceId}
                disabled={disabled}
              />
              <Button variant="secondary" onClick={() => setOpen(false)}>
                Exit presentation
              </Button>
            </div>
          </div>
          {slides[selected] && (
            <Slide
              key={selected}
              notebook={notebook}
              organizationSlug={organizationSlug}
              workspaceId={workspaceId}
              slide={slides[selected]}
            />
          )}
          <div className="flex flex-wrap items-center justify-center gap-3 pt-3">
            <Button
              variant="secondary"
              disabled={selected === 0}
              onClick={previous}
            >
              <ChevronLeft />
              Previous
            </Button>
            <span role="status" className="text-sm">
              Slide {selected + 1} of {slides.length}
            </span>
            <Select
              value={String(selected)}
              onValueChange={(value) => setIndex(Number(value))}
            >
              <SelectTrigger
                aria-label="Go to slide"
                className="w-auto max-w-64"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {slides.map((slide, number) => (
                  <SelectItem key={number} value={String(number)}>
                    {number + 1}. {slide.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="secondary"
              disabled={selected === slides.length - 1}
              onClick={next}
            >
              Next
              <ChevronRight />
            </Button>
            <span className="text-xs text-coval-text-muted">
              Arrow keys to navigate · Esc to exit
            </span>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
